<template>
  <section class="">

    <div class="mt-[30px] flex gap-[5px] justify-center flex-wrap">
      <span v-for="(item, idx) in periodList" :key="idx" @click="changePeriod(item.id)"
        :class="`w-[60px] h-[35px] rounded-[4px] flex items-center justify-center text-[10px] transition-all duration-200 ${
          item.isActive 
            ? 'bg-primary text-white pointer-events-none' 
            : isChangingPeriod 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-[#E5E5E5] text-[#6A6A6A] cursor-pointer hover:bg-gray-200'
        } sm:text-[12px] sm:w-[100px] md:text-[14px]`">
        <span v-if="isChangingPeriod && item.isActive" class="animate-spin mr-1">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
        {{ item.text }}
      </span>

    </div>
    <!-- middle packages -->
    <div v-if="packages.length && !isLoading" class="package-container gap-[1em] mx-auto mt-10">
      <div
        class="flex flex-col mt-2 w-[100%] max-w-[330px] max-h-[400px] bg-[#FCFCFC] border border-gray-200 shadow dark:bg-gray-800 dark:border-gray-700"
        v-for="(packageData, index) in packages" :key="packageData.id || index">
        <packageCard :packageData="packageData" @changeCurrentPackage="changeCurrentPackage" />
      </div>
    </div>
    
    <!-- skeleton packages card at loading -->
    <div v-if="isLoading" class="package-container gap-[1em] mx-auto mt-10">
      <div
        class="flex flex-col mt-2 w-[100%] max-w-[330px] bg-[#FCFCFC] border border-gray-200 shadow dark:bg-gray-800 dark:border-gray-700"
        v-for="(_, index) in Array(3)" :key="`skeleton-${index}`">
        <packageCard :isLoading="isLoading" />
      </div>
    </div>

    <!-- bottom discount coupon  -->
    <div class="flex flex-col items-center gap-[1em] mx-auto mt-10 max-w-[480px]">
      <h1 class="font-semibold">{{ $t('user.upgrade.have_discount_coupon') }}</h1>
      
      <!-- Package selection warning -->
      <div v-if="!currentPackage" class="w-full p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div class="flex items-center gap-2 text-yellow-800">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
          </svg>
          <span class="text-sm font-medium">{{ $t('user.upgrade.select_package_first') || 'Please select a package first before applying a coupon' }}</span>
        </div>
      </div>
      
      <div class="flex flex-col gap-2 w-full sm:flex-row ">
        <input 
          type="text" 
          v-model="discountCoupon"
          :disabled="!currentPackage"
          :class="`sm:w-[12rem] lg:min-w-[20rem] flex-1 bg-[#FFFFFF] border outline-none text-gray-900 rounded-[7px] text-sm block pr-3 p-2.5 text-center transition-colors duration-200 ${
            !currentPackage 
              ? 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50' 
              : 'border-[#D8D8D8] focus:border-blue-500'
          }`"
          :placeholder="currentPackage ? 'Ex: CEZMA23' : $t('user.upgrade.select_package_first')" />
        <div class="flex gap-2">
          <button 
            @click="applayDiscountCoupon" 
            :disabled="isLoadingCoupon || !currentPackage || !discountCoupon.trim()"
            :class="`flex-1 sm:px-4 sm:py-3 sm:text-md px-3 py-2 text-sm text-white rounded-md transition-all duration-200 ${
              !currentPackage || !discountCoupon.trim()
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#0963FF] hover:bg-blue-700 disabled:bg-[#2d3b82c5] disabled:animate-pulse'
            }`">
            {{ $t('general.apply') }}
          </button>
          <button 
            @click="clearDiscount"
            :disabled="!discount"
            :class="`rounded-[8px] h-[50px] w-[50px] flex items-center justify-center cursor-pointer transition-all duration-200 ${
              !discount 
                ? 'bg-gray-200 cursor-not-allowed' 
                : 'bg-[#FF1F001A] hover:bg-red-100'
            }`">
            <img src="../../../assets/icons/Delete.svg" alt="">
          </button>
        </div>
      </div>

      <!-- Coupon validation errors -->
      <div v-if="couponState === 'notApplied'" class="w-full p-3 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex items-center gap-2 text-red-800">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
          </svg>
          <span class="text-sm font-medium">{{ $t('general.validation_msg.couponRequired') }}</span>
        </div>
      </div>
      
      <!-- Coupon package validation error -->
      <div v-if="couponState === 'invalidPackage'" class="w-full p-3 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2 text-red-800">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
            </svg>
            <span class="text-sm font-medium">
              {{ $t('user.upgrade.coupon_invalid_for_package') || 'كوبون الخصم مخصص لـ' }}
            </span>
          </div>

          <div class="flex flex-col gap-1.5 pl-7">
            <div v-for="(pkg, index) in couponPackages" :key="index" 
                 class="flex items-center gap-2 text-red-700 font-medium text-sm">
              <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>
              <span>{{ currentLanguage === 'ar' ? pkg.name_ar : pkg.name_en }}</span>
              <span class="text-red-600">/</span>
              <span>{{ periodList.find(p => p.id === pkg.months_period)?.text }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full">

        <div v-if="discount?.percent" class=" text-[#31BA45] text-[14px] mt-[20px] ">
          <div class="w-full flex justify-between mb-1">
            <span>{{ $t('general.the_discount') }}</span>
            <span>{{ `${$t('general.discount')} ${discount?.percent} %` }} </span>
          </div>
          <div>{{ $t('general.congrats_discount') }}</div>
        </div>

        <div v-if="currentPackagePrice"
          class="w-full flex justify-between text-[14px] pt-1 border-t border-[#D8D8D8] mt-[20px] font-semibold">
          <span>{{ $t('general.total') }}</span>
          <span>
            <!-- {{ parseFloat(currentPackagePrice) - (parseFloat(currentPackagePrice) * parseFloat(discount?.percent) / 100) }} {{ $t('general.pound') }} -->
            {{ discount?.percent ? parseFloat(currentPackagePrice) - (parseFloat(currentPackagePrice) * parseFloat(discount?.percent) / 100) : currentPackagePrice }} {{ $t('general.pound') }}
          </span>
        </div>
      </div>

      <button
        class="mt-[15px] text-white bg-primary rounded-[8px] w-full h-[50px] font-semibold flex items-center justify-center sm:text-[19px]"
        @click="confirm">{{ $t('general.confirm') }}</button>

    </div>



  </section>
</template>

<script>

import packageCard from '@/components/user/upgrade/packageCard.vue';
import IconExclamation from '@/assets/shared/icons/IconExclamation.vue'
import checkUserAuthentication from "@/middleware/checkUserAuthentication.js";
import { SPECIFICROLES, MAINROLES } from "@/helpers";
import { useGlobalStore } from "~/store/Modules/global";


export default {
  name: 'upgrade',
  props: {
    type: String,
    currentStoreId: [String, Number],
    currentPackage: {
      type: Object,
      default: null
    },
    initialPeriod: {
      type: Number,
      default: 1
    }
  },
  setup() {
    definePageMeta({
      layout: "user",
      middleware: checkUserAuthentication({ specificRole: SPECIFICROLES.user, role: MAINROLES.user })
    });
    return {
      router: useRouter(),
      route: useRoute(),
      currentLanguage: useGlobalStore()?.localStorageCopy?.language || "ar",
    };
  },
  components: {
    packageCard,
    IconExclamation,
  },
  data() {
    return {
      isLoading: false,
      isChangingPeriod: false, // Add this new loading state
      packages: [],
      discountCoupon: '',
      url: '',
      isLoadingCoupon: false,
      couponState: 'init',//init,notApplied,applied
      discount: null,
      period: this.initialPeriod,
      currentPackagePrice: null,
      currentPackage: null,
      couponPackages: [], // Add this to store packages from coupon response
      periodList: [
        { id: 1, text: this.$t('user.upgrade.per_month'), isActive: this.initialPeriod === 1 },
        { id: 3, text: this.$t('user.upgrade.per_3_month'), isActive: this.initialPeriod === 3 },
        { id: 6, text: this.$t('user.upgrade.per_6_month'), isActive: this.initialPeriod === 6 },
        { id: 12, text: this.$t('user.upgrade.per_12_month'), isActive: this.initialPeriod === 12 },
      ]

    };
  },


  mounted() {
    console.log('SetPackages mounted with:', {
      type: this.type,
      initialPeriod: this.initialPeriod,
      currentPackage: this.currentPackage,
      period: this.period
    });
    this.getPackages();
  },
  methods: {
    changeCurrentPackage(currentPackage) {
      this.currentPackagePrice = parseFloat(currentPackage.price)
      this.currentPackage = currentPackage;
      
      // Clear coupon when package changes
      this.clearDiscount();
    },


    changePeriod(id) {
      // Prevent multiple clicks while changing period
      if (this.isChangingPeriod) return;
      
      this.isChangingPeriod = true;
      
      // Clear current package selection when changing period
      this.currentPackage = null;
      this.currentPackagePrice = null;
      
      // Clear discount when changing period
      this.discount = null;
      this.discountCoupon = '';
      this.couponPackages = [];
      this.couponState = 'init';
      
      this.period = id;

      this.periodList = this.periodList.map((item) => {
        if (item.id == id) item.isActive = true;
        else item.isActive = false;

        return item;
      });
      
      // Clear packages immediately to show skeleton
      this.packages = [];
      
      // Load packages for the new period
      this.loadPackagesForPeriod(id);
    },
    confirm() {


      if (this.currentPackage?.id) {
        const upgrade = {
          setp: 2,
          packageId: this.currentPackage?.id,
        }

        if (+this.discount?.percent == 100) {
          const payload = {
            package_id:this.currentPackage?.id,
            coupon_id:this.discount?.id
          }
          this.handleCheckout(payload)

          return;
        }
        else if (this.discount?.id) upgrade.couponId = this.discount?.id
        sessionStorage.setItem('upgrade', JSON.stringify(upgrade));
        
        // Emit package details - parent component will handle the flow
        const packageInfo = {
          packageId: this.currentPackage?.id,
          packageData: this.currentPackage,
          period: this.period,
          months_period: this.currentPackage?.months_period || this.period,
          price: this.currentPackagePrice,
          discount: this.discount
        };
        
        this.$emit('packageSelected', packageInfo);
        
        // Also emit changePage for backward compatibility, but parent should handle via packageSelected
        this.$emit('changePage');
        // this.$router.replace(this.ROUTESNAMES.user.upgradePayment);

      } else {
        this.notify({ type: 'error', msg: this.$t('general.choose_package_err') });

      }


    },

    handleCheckout(payload) {

      localStorage.setItem('allowToCreate', JSON.stringify(true));


      this.useLocalStorage({ type: 'set', key: 'allowToCreate', value: true }); //permission to access page store-settings/create


      this.isProcessing = true;
      this.makeRequest({
        owner: this.MAINROLES.user,
        url: this.APIREQUESTS.reseller.storeCheckout.url,
        method: this.APIREQUESTS.reseller.storeCheckout.method,
        formData: payload
      })
        .then((res) => {
          if (!res || !res?.hasOwnProperty('status')) {
            return;
          } else if (!res?.status) {
            this.notify({ type: 'error', msg: res.message, });
          } else {
            // succeeded


            //some changes in user in local, upgrade user to reseller and add selectedPackage 
            let user = this.useLocalStorage({ type: "get", key: 'user' });

            user = {
              ...user,
              role: 'reseller',//change role of user to reseller untill login again to change user object form response
              selectedPackage: this.packageData || {},//save package that reseller was selected in localstorage, 
              packageWasSelected: true,//permission to access page store-settings/create
            }
            this.useLocalStorage({ type: 'set', key: 'user', value: user });
            this.useLocalStorage({ type: 'set', key: 'allowToCreate', value: true }); //permission to access page store-settings/create
          
            this.$router.replace(`${this.ROUTESNAMES.user.storeSettings}/create`)

          }
        })
        .catch((err) => { //not succeeded
          // console.error(err);
          //case of Unauthenticated
          this.notify({
            type: 'error',
            msg: err?.data?.message || this.$t("general.unknown_error"),
          });
        }).finally(() => {
          this.isProcessing = false
          // this.setCouponState('init')
          this.closePaymentModal()
        })



    },










    setCouponState(newVal) {
      this.couponState = newVal
    },
    getPackages() {
      this.isLoading = true;
      // Clear packages immediately when starting to load
      this.packages = [];
      
      // Simple single period loading - parent component handles finding correct period
      this.loadPackagesForPeriod(this.period);
    },


    loadPackagesForPeriod(periodId) {
      let url = '';
      switch (this.type) {
        case 'upgrade':
          url = `${this.APIREQUESTS.panel.getPackages.url}?months_period=${periodId}&change_type=${this.type}&store_id=${this.currentStoreId}`;
          break;
        case 'downgrade':
          url = `${this.APIREQUESTS.panel.getPackages.url}?months_period=${periodId}&change_type=${this.type}&store_id=${this.currentStoreId}`;
          break;
        case 'renew':
          url = `${this.APIREQUESTS.panel.getPackages.url}?months_period=${periodId}&change_type=renew&store_id=${this.currentStoreId}`;
          break;
        default:
          url = `${this.APIREQUESTS.panel.getPackages.url}?months_period=${periodId}`;
          break;
      }
      
      this.makeRequest({
        owner: this.MAINROLES.user,
        url: url,
        method: this.APIREQUESTS.panel.getPackages.method,
      })
        .then((res) => {
          if (!res || !res?.hasOwnProperty('status')) {
            return;
          } else if (!res?.status) {
            this.notify({ type: res?.error_code == 1006 ? 'info' : 'error', msg: res.message });
            if (res?.error_code == 1006) {
              localStorage.setItem('allowToCreate', JSON.stringify(true));

              //case of authenticated and bought package but not create a store
              //save package that reseller was selected in localstorage
              let user = this.useLocalStorage({ type: 'get', key: 'user' });
              user = {
                ...user,
                selectedPackage: res?.data[0] || {},
                packageWasSelected: true //permission to access page store-settings/create
              };
              this.useLocalStorage({ type: 'set', key: 'user', value: user });

              //goto create store
              this.$router.replace(`${this.ROUTESNAMES.user.storeSettings}/create`);
            }
          } else {
            // succeeded
            this.packages = res?.data || [];
          }
        })
        .catch((err) => {
          //not succeeded
          this.notify({ type: 'error', msg: err?.data?.message || this.$t("general.unknown_error") });
        })
        .finally(() => {
          this.isLoading = false;
          this.isChangingPeriod = false; // Reset period changing state
        });
    },

    clearDiscount() {
      this.discountCoupon = '';
      this.discount = null;
      this.couponPackages = [];
      this.setCouponState('init');
    },


    applayDiscountCoupon() {
      if (!this.discountCoupon) return; // validation of discount Coupon is empty to send a request
      
      // Check if package is selected
      if (!this.currentPackage) {
        this.notify({ type: 'error', msg: this.$t('user.upgrade.select_package_first') || 'Please select a package first' });
        return;
      }

      this.isLoadingCoupon = true;
      this.makeRequest({
        owner: this.MAINROLES.user,
        url: `${this.APIREQUESTS.reseller.applyCoupon.url}?code=${this.discountCoupon}`,
        method: this.APIREQUESTS.reseller.applyCoupon.method,
      })
        .then((res) => {
          if (!res?.status) {
            this.notify({ type: 'error', msg: res.message });
            this.setCouponState('notApplied');
          } else {
            // Check if coupon is valid for the selected package
            const couponPackages = res?.data?.packages || [];
            this.couponPackages = couponPackages;
            
            // If coupon has specific packages, validate the selected package
            if (couponPackages.length > 0) {
              const isPackageValid = couponPackages.some(pkg => pkg.id === this.currentPackage.id);
              
              if (!isPackageValid) {
                this.notify({ 
                  type: 'error', 
                  msg: this.$t('user.upgrade.coupon_invalid_for_package') + ' ' + couponPackages.map(pkg => this.currentLanguage === 'ar' ? pkg.name_ar : pkg.name_en).join(', ')
                });
                this.setCouponState('invalidPackage');
                return;
              }
              
              // Also check if the coupon is valid for the current period
              const selectedPackageInList = this.packages.find(p => p.id === this.currentPackage.id);
              const packagePeriod = selectedPackageInList?.months_period || this.period;
              
              // Check if any of the coupon packages match both the package ID and period
              const isPeriodValid = couponPackages.some(pkg => {
                const couponPackagePeriod = pkg.months_period || this.period;
                return pkg.id === this.currentPackage.id && couponPackagePeriod === packagePeriod;
              });
              
              if (!isPeriodValid) {
                this.notify({ 
                  type: 'error', 
                  msg: this.$t('user.upgrade.coupon_invalid_for_period') || 'This coupon is not valid for the selected package period' 
                });
                this.setCouponState('invalidPackage');
                return;
              }
            }
            
            // succeeded - coupon is valid for the package
            this.discount = {
              percent: res?.data?.discount,
              id: res?.data?.id,
            };
            this.setCouponState('applied');
            this.notify({ type: 'success', msg: this.$t("user.upgrade.discount_is_applied") });
          }
        })
        .catch((err) => {
          //not succeeded
          this.notify({ type: 'error', msg: err?.data?.message || this.$t("general.unknown_error") });
          this.setCouponState('notApplied');
        })
        .finally(() => {
          this.isLoadingCoupon = false;
        });
    },

  },

  watch: {
    initialPeriod: {
      handler(newPeriod) {
        if (newPeriod && newPeriod !== this.period) {
          // Update current period and period list when parent updates
          this.period = newPeriod;
          this.periodList = this.periodList.map((item) => {
            item.isActive = (item.id === newPeriod);
            return item;
          });
          
          // Load packages for the new period
          this.getPackages();
        }
      },
      immediate: false
    },
    
    period: {
      handler(val) {
        if (val) {
          this.getPackages();
        }
      }
    },
  },
};
</script>
<style scoped>
.package-container {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  justify-items: center;
  grid-gap: 0.9em;
  justify-content: center;
  align-items: start; /* Align items to the top */
}

/* Ensure cards maintain consistent width and initial height */
.package-container > div {
  width: 100%;
  max-width: 330px;
  min-height: 400px; /* Set minimum height for consistency */
  height: fit-content; /* Allow height to grow with content */
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease; /* Smooth transition for height changes */
}

/* Ensure the package card takes full height */
.package-container > div > * {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Responsive height adjustments */
@media (max-width: 640px) {
  .package-container > div {
    min-height: 380px;
  }
}

@media (min-width: 641px) and (max-width: 1024px) {
  .package-container > div {
    min-height: 390px;
  }
}

@media (min-width: 1025px) {
  .package-container > div {
    min-height: 400px;
  }
}
</style>